/**
 * PublikaYA Analytics Tracker
 * Sistema ligero de tracking de visitas a páginas
 */

(function() {
  'use strict';

  // Configuración
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBaseUrl = isLocalHost 
    ? `${window.location.protocol}//${window.location.hostname}:3000/api/v1`
    : 'https://publikaya.com/api/v1';
  
  const CONFIG = {
    endpoint: `${apiBaseUrl}/analytics/track-page-view`,
    sessionDuration: 30 * 60 * 1000, // 30 minutos en millisegundos
    debugMode: false // Cambiar a true para ver logs
  };

  /**
   * Clase principal del tracker
   */
  class PublikaYATracker {
    constructor() {
      this.sessionId = this.getOrCreateSessionId();
      this.hasTrackedThisPage = false;
    }

    /**
     * Genera un ID único (UUID v4 simple)
     */
    generateId() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }

    /**
     * Obtiene o crea un session_id en sessionStorage
     * Se renueva automáticamente después de 30 minutos de inactividad
     */
    getOrCreateSessionId() {
      const storageKey = 'pya_session';
      const now = Date.now();

      try {
        const stored = sessionStorage.getItem(storageKey);
        
        if (stored) {
          const data = JSON.parse(stored);
          const elapsed = now - data.timestamp;
          
          // Si la sesión aún es válida (menos de 30 min), reutilizar
          if (elapsed < CONFIG.sessionDuration) {
            // Actualizar timestamp para extender la sesión
            data.timestamp = now;
            sessionStorage.setItem(storageKey, JSON.stringify(data));
            return data.id;
          }
        }
      } catch (e) {
        this.log('Error reading session:', e);
      }

      // Crear nueva sesión
      const newSessionId = this.generateId();
      const sessionData = {
        id: newSessionId,
        timestamp: now
      };

      try {
        sessionStorage.setItem(storageKey, JSON.stringify(sessionData));
      } catch (e) {
        this.log('Error storing session:', e);
      }

      return newSessionId;
    }

    /**
     * Log condicional solo en modo debug
     */
    log(...args) {
      if (CONFIG.debugMode) {
        console.log('[PublikaYA Analytics]', ...args);
      }
    }

    /**
     * Registra la visita actual
     */
    async trackPageView() {
      // NO TRACKEAR EN DESARROLLO LOCAL
      const isLocalDevelopment = window.location.hostname === 'localhost' || 
                                  window.location.hostname === '127.0.0.1' ||
                                  window.location.hostname.includes('local');
      
      if (isLocalDevelopment) {
        console.log('[PublikaYA Analytics] 🚫 Tracking desactivado en desarrollo local');
        return;
      }
      
      // Evitar tracking duplicado en la misma página
      if (this.hasTrackedThisPage) {
        this.log('Page already tracked, skipping');
        return;
      }

      const data = {
        page_url: window.location.pathname + window.location.search,
        page_title: document.title || 'Sin título',
        referrer: document.referrer || 'direct',
        session_id: this.sessionId,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        // Datos adicionales para detección de bots
        user_agent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        touch_support: 'ontouchstart' in window,
        webgl_vendor: this.getWebGLVendor()
      };

      this.log('Tracking page view:', data);

      try {
        const response = await fetch(CONFIG.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data),
          // No esperar respuesta para no bloquear la página
          keepalive: true
        });

        if (response.ok) {
          this.hasTrackedThisPage = true;
          this.log('Page view tracked successfully');
        } else {
          this.log('Tracking failed with status:', response.status);
        }
      } catch (error) {
        // Silencioso: no interrumpir la experiencia del usuario
        this.log('Tracking error:', error);
      }
    }

    /**
     * Obtiene el vendor de WebGL (útil para detectar headless browsers)
     */
    getWebGLVendor() {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return 'none';
        
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) return 'unknown';
        
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      } catch (e) {
        return 'error';
      }
    }

    /**
     * Trackea clic en botón de llamada (teléfono)
     */
    async trackPhoneClick(businessId, businessName) {
      console.log('[Analytics] 📞 trackPhoneClick llamado', { businessId, businessName });
      
      // CAMBIAR A true PARA TESTING EN LOCALHOST
      const ENABLE_LOCAL_TESTING = false;
      
      const isLocalDevelopment = window.location.hostname === 'localhost' || 
                                  window.location.hostname === '127.0.0.1';
      
      if (isLocalDevelopment && !ENABLE_LOCAL_TESTING) {
        console.log('[Analytics] 🚫 Tracking deshabilitado en desarrollo local');
        return;
      }
      
      console.log('[Analytics] ✅ Tracking habilitado, enviando...');

      const data = {
        event_type: 'phone_click',
        business_id: businessId,
        business_name: businessName,
        page_url: window.location.pathname + window.location.search,
        session_id: this.sessionId,
        timestamp: new Date().toISOString()
      };

      this.log('Tracking phone click:', data);

      try {
        const endpoint = `${apiBaseUrl}/analytics/track-click`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          keepalive: true
        });

        if (response.ok) {
          this.log('Phone click tracked successfully');
        }
      } catch (error) {
        this.log('Phone click tracking error:', error);
      }
    }

    /**
     * Trackea clic en botón de WhatsApp
     */
    async trackWhatsAppClick(businessId, businessName) {
      console.log('[Analytics] 💬 trackWhatsAppClick llamado', { businessId, businessName });
      
      // CAMBIAR A true PARA TESTING EN LOCALHOST
      const ENABLE_LOCAL_TESTING = false;
      
      const isLocalDevelopment = window.location.hostname === 'localhost' || 
                                  window.location.hostname === '127.0.0.1';
      
      if (isLocalDevelopment && !ENABLE_LOCAL_TESTING) {
        console.log('[Analytics] 🚫 Tracking deshabilitado en desarrollo local');
        return;
      }
      
      console.log('[Analytics] ✅ Tracking habilitado, enviando...');

      const data = {
        event_type: 'whatsapp_click',
        business_id: businessId,
        business_name: businessName,
        page_url: window.location.pathname + window.location.search,
        session_id: this.sessionId,
        timestamp: new Date().toISOString()
      };

      this.log('Tracking WhatsApp click:', data);

      try {
        const endpoint = `${apiBaseUrl}/analytics/track-click`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          keepalive: true
        });

        if (response.ok) {
          this.log('WhatsApp click tracked successfully');
        }
      } catch (error) {
        this.log('WhatsApp click tracking error:', error);
      }
    }

    /**
     * Inicializa el tracker
     */
    init() {
      // Esperar a que la página esté completamente cargada
      if (document.readyState === 'complete') {
        this.trackPageView();
      } else {
        window.addEventListener('load', () => {
          this.trackPageView();
        });
      }

      // Extender sesión en cada interacción del usuario
      ['click', 'scroll', 'keydown'].forEach(event => {
        document.addEventListener(event, () => {
          this.getOrCreateSessionId(); // Actualiza timestamp
        }, { passive: true, once: false });
      });
    }
  }

  // Auto-inicializar cuando el script se carga
  const tracker = new PublikaYATracker();
  tracker.init();

  // Exponer globalmente para uso manual si es necesario
  window.PublikaYATracker = PublikaYATracker;
  window.pyaTracker = tracker;

})();
